/**
 * identity-service `/verify-email` client (bl-813).
 *
 * Hits `POST {VITE_IDENTITY_SERVICE_URL}/verify-email` with `{ token }` — the
 * single-use email-verification token carried in the `?token=` query param of
 * the link identity-service emails on register. Mirrors `api/register.ts` /
 * `api/login.ts` exactly: same `VITE_IDENTITY_SERVICE_URL` base-URL resolution
 * (trailing-slash-stripped), same fetch/try-catch convention, same
 * status-classified discriminated result.
 *
 * SECURITY: the token is sent ONLY in the request body and is NEVER logged,
 * stored, or echoed. Every server failure (invalid / expired / already-used /
 * 0-row flip per bl-813 AC7) collapses to the uniform `error` branch — the UI
 * shows one non-enumerating message, so a caller can't probe token validity.
 */

/**
 * Discriminated outcome of a verify-email attempt, classified by HTTP status.
 *  - `success` — 200, the email is verified (or was already verified).
 *  - `error`   — any non-200 (invalid/expired/used token, 0-row flip, 5xx,
 *                network failure, unconfigured base URL). Uniform, non-enumerating.
 */
export type VerifyEmailResult = { kind: 'success' } | { kind: 'error' };

function identityServiceUrl(): string {
  const base = import.meta.env.VITE_IDENTITY_SERVICE_URL;
  if (!base) {
    // Surfaced as a generic error in the UI; the deploy build-arg wires this.
    throw new Error('VITE_IDENTITY_SERVICE_URL is not configured');
  }
  return base.replace(/\/$/, '');
}

/**
 * `POST /verify-email`. Submits `{ token }` and classifies the response into the
 * two UI branches. The token never leaves the request body.
 */
export async function verifyEmail(token: string): Promise<VerifyEmailResult> {
  let res: Response;
  try {
    res = await fetch(`${identityServiceUrl()}/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
  } catch {
    // Network/CORS failure — generic, no detail (no token, no internals).
    return { kind: 'error' };
  }

  switch (res.status) {
    case 200:
      return { kind: 'success' };
    default:
      // 400 / 401 / 404 / 410 (expired) / 500 / unexpected → uniform error.
      return { kind: 'error' };
  }
}
